/** /trip_types/ — parent-scoped Trip_Type ("Filters") catalog.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). Trip types are owned by a ROOT operator (a franchise
 * parent or a standalone TO — never a licensee). Writes carry an optimistic-
 * concurrency token (`expected_updated_ts`); a drift returns 409.
 */
import { apiDelete, apiGet, client } from './client';
import type { NamedRef, TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

export interface TripTypeListItem {
  id: number;
  /** Owning tenant; null for unassigned/orphaned trip types (`Trip_Type.tour_operator`
   *  is nullable). The list grid renders null as '—'. */
  tenant: TenantRef | null;
  name: string;
  custom_id: string | null;
  has_image: boolean;
  /** Shown in the client apps under the filter (visible column). */
  description: string | null;
  /** Branch.io deep link (hidden by default). */
  branchio_link: string | null;
  sequence: number;
  updated_ts: string | null;
}

/** Image path chunks — compose the URL as `${AWS_IMAGE_BASE_URL}${p}${s}`
 *  (see `imageUrl` in `@/utils/imageUrl`). */
export interface ImageRef {
  p: string;
  s: string;
}

export interface TripTypeDetail extends TripTypeListItem {
  image: ImageRef | null;
  atag: NamedRef[];
  ext_id: string | null;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<TripTypeListItem>> {
  return apiGet<PaginatedResponse<TripTypeListItem>>(`/trip_types/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<TripTypeDetail> {
  return apiGet<TripTypeDetail>(`/trip_types/${id}/`);
}

/** The `_json` part of a trip-type write (also the whole body of a metadata-only
 *  PATCH). `tour_operator` is required on create (a root operator). */
export interface TripTypeWriteJson {
  tour_operator?: number;
  name?: string;
  description?: string | null;
  custom_id?: string | null;
  atag?: { id: number }[];
  expected_updated_ts?: string | null;
}

/** POST /trip_types/ — always multipart; the image is required on create. */
export async function create(body: TripTypeWriteJson, image: File): Promise<TripTypeDetail> {
  const fd = new FormData();
  fd.append('_json', JSON.stringify(body));
  fd.append('image', image);
  const r = await client.post<TripTypeDetail>('/trip_types/', fd, {
    headers: { 'Content-Type': undefined },
  });
  return r.data;
}

/** PATCH /trip_types/{id}/ — plain JSON for metadata; multipart when an image
 *  File is staged (to swap the image too). Pass `expected_updated_ts` for
 *  optimistic concurrency; a drift returns 409. */
export async function update(
  id: number,
  body: TripTypeWriteJson,
  image?: File | null,
): Promise<TripTypeDetail> {
  if (image) {
    const fd = new FormData();
    fd.append('_json', JSON.stringify(body));
    fd.append('image', image);
    const r = await client.patch<TripTypeDetail>(`/trip_types/${id}/`, fd, {
      headers: { 'Content-Type': undefined },
    });
    return r.data;
  }
  const r = await client.patch<TripTypeDetail>(`/trip_types/${id}/`, body);
  return r.data;
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/trip_types/${id}/`);
}

export function reorder(tourOperator: number, orderedIds: number[]): Promise<{ status: string }> {
  const fd = { tour_operator: tourOperator, ordered_ids: orderedIds };
  return client.post('/trip_types/reorder/', fd).then((r) => r.data as { status: string });
}
